import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from "react";
import { IconButton, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Stack from "@mui/material/Stack";
import { DimensionWithCosts } from "@/services/cloudchipr.api";
import { CategoryWithDimensionId } from "@/store/dimensions/utils/types/common";
import { useAppDispatch } from "@/store/hooks";
import { updateDimensionOrCategoryName } from "@/store/dimensions/thunks/updateDimensionOrCategoryName";

interface DimensionCategoryEditFieldProps {
  data: DimensionWithCosts | CategoryWithDimensionId;
  onSubmit(): void;
}

export const DimensionCategoryEditField: FC<
  DimensionCategoryEditFieldProps
> = ({ data, onSubmit }) => {
  const [name, setName] = useState<string>(data.name);
  const dispatch = useAppDispatch();

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit();

    if (data.name === name) {
      return;
    }

    dispatch(
      updateDimensionOrCategoryName({
        ...data,
        name,
      }),
    );
  }, [onSubmit, data, name, dispatch]);

  return (
    <form onSubmit={handleSubmit} onKeyDownCapture={stopPropagation}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <TextField
          fullWidth
          variant="outlined"
          size="xsmall"
          name="name"
          value={name}
          onChange={handleChange}
          error={!name}
          autoFocus
          sx={inputStyles}
        />

        <IconButton
          size="small"
          disabled={!name}
          sx={iconButtonStyles}
          onClick={handleSubmit}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
      </Stack>
    </form>
  );
};

const stopPropagation = (e: KeyboardEvent) => e.stopPropagation();

const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
const inputStyles = {
  "& .MuiInputBase-root": {
    height: 24,
    width: 200,
  },
  "& .MuiInputBase-input": {
    padding: "0 8px",
    height: "100%",
    boxSizing: "border-box",
  },
};
